<template>
<div class="page-wrapper">
	<div class="page page-settings">
		<div class="container">
			<div class="page-header">
				<h1 class="page-title">
					<router-link class="text" to="/settings">Settings</router-link>
					<i class="separator material-icons">chevron_right</i>
					<span class="page-breadcrumb">{{ title }}</span>
				</h1>
			</div>
			<div class="page-content">
				<div class="box p-0" v-if="!user">
					<div class="is-loading p-3 text-center"></div>
				</div>
				<div class="" v-else>
					<!-- Inventory Management -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Stocks Management</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper" v-if="!!userSettings">
									<div class="container-fluid p-0">
										<div class="row">
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Deduction Trigger</b></label>
													<select class="form-control" v-model="userSettings.stock.deduction_trigger">
														<option value="completed">Order is Completed</option>
														<option value="processing">Order is Processing</option>
														<option value="pending">Order is Pending / Order is Placed</option>
													</select>
													<div class="pc-input-desc">{{ $t('settings.stock.deductionDesc') }}</div>
												</div>
											</div>
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Restore Trigger</b></label>
													<select class="form-control" v-model="userSettings.stock.restore_trigger">
														<option value="cancelled">Order is Cancelled</option>
													</select>
													<div class="pc-input-desc">{{ $t('settings.stock.restoreDesc') }}</div>
												</div>
											</div>
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Visibility</b></label>
													<select class="form-control" v-model="userSettings.stock.visible">
														<option :value="true">Visible to visitor</option>
														<option :value="false">Hide the stock to visitor</option>
													</select>
												</div>
											</div>
										</div>
										<div>
											<div class="form-group text-right">
												<button
													type="button"
													class="btn btn-primary"
													:class="[{ 'is-loading': isSavingSettings }]"
													@click="saveSettings">
													<i class="material-icons">save</i><span>{{ $t('Save') }}</span>
												</button>
											</div>
										</div>
									</div>
								</div>
								<div class="is-loading large dark mt-3 mb-4" v-if="!userSettings"></div>
							</div>
						</div>
					</div>

					<!-- Auto Cancel Order -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Order Auto Cancel</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="form-group">
											<label class="label-checkbox">
												<input type="checkbox" class="input-checkbox input-checkbox-large" v-model="autoCancel.enabled">
												<span></span>
												<span class="input-checkbox-text">Enable order auto cancel</span>
											</label>
										</div>
										<div class="form-group">
											<label><b>Cancel Order After (days):</b></label>
											<input type="number" min="1" max="14" class="input form-control input-small" v-model="autoCancel.value">
										</div>
										<div>
											<div class="form-group text-right">
												<button type="button" class="btn btn-primary" :class="[{ 'is-loading': isSavingAutoCancel }]" @click="saveAutoCancel"><i class="material-icons">save</i><span>{{ $t('Save') }}</span></button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Email Settings -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Transactional Email</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="form-group">
											<label class="label-checkbox">
												<input type="checkbox" class="input-checkbox input-checkbox-large" v-model="transactionEmailSetting.enabled">
												<span></span>
												<span class="input-checkbox-text">Enable send email notification to customer</span>
											</label>
										</div>
										<div class="form-group">
											<label><b>Email Reply-To</b></label>
											<input type="text" class="input form-control input-medium" placeholder="Reply-to Email" v-model="transactionEmailSetting.reply_to">
										</div>
										<div>
											<div class="form-group text-right">
												<button type="button" class="btn btn-primary" :class="[{ 'is-loading': isSavingEmailTransactionalSetting }]" @click="saveEmailTransactionalSetting">
													<i class="material-icons">save</i><span>{{ $t('Save') }}</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Security -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Security</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="row">
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Add IP to Block</b></label>
													<textarea
														class="form-control"
														placeholder="192.168.XXX.XXX"
														v-model="ipAddress">
													</textarea>
													<button
														type="button"
														class="btn btn-default mt-3"
														@click="addIp">
														Block IP
													</button>
												</div>
											</div>
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Blocked IP Address</b></label>
													<div class="selected-options">
														<div
															class="selected-option bg-grey"
															v-for="(ip, index) in blockedIps"
															:key="ip">
															<span>{{ ip }}</span>
															<i
																class="material-icons selected-option-remove"
																v-tooltip="'Unblock this IP Address'"
																@click="removeIp(index)">close</i>
														</div>
														<div v-if="blockedIps.length === 0">
															- Your store is open to anyone -
														</div>
													</div>
												</div>
											</div>
											<div class="col-12">
												<div class="form-group text-right">
													<button
														type="button"
														class="btn btn-primary"
														:class="{ 'is-loading': isSavingSettings }"
														@click="saveSettings">
														<i class="material-icons">save</i>
														<span>{{ $t('Save') }}</span>
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- E-Payment Settings -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">E-Payment Settings</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="row">
											<div class="col-sm-6">
												<div class="form-group">
													<label><b>Status Change</b></label>
													<select
														class="form-control input-medium"
														v-model="instantPayment.status">
														<option
															v-for="(item, index) in statusChangeOptions"
															:key="index"
															:value="item.value">
															{{ item.label }}
														</option>
													</select>
												</div>
											</div>
											<div class="col-sm-6">
												<div class="form-group">
													<label><b>Enable Logistics</b></label>
													<label class="label-checkbox">
														<input
															type="checkbox"
															class="input-checkbox input-checkbox-large"
															v-model="instantPayment.logistic_enabled">
														<span></span>
														<span class="input-checkbox-text">Enable E-Payment for Ostock Logistics (SiCepat, J&T, JNE)</span>
													</label>
												</div>
											</div>
										</div>
										<div>
											<div class="form-group text-right">
												<button
													type="button"
													class="btn btn-primary"
													:class="[{ 'is-loading': isSavingInstant }]"
													@click="saveInstant">
													<i class="material-icons mr-1">save</i>
													<span>{{ $t('Save') }}</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Custom Meta Tags -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Custom Meta Tags</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<meta-tags
											:show-title="false"
											v-model="customMetaTags"
										/>
										<div>
											<div class="form-group text-right">
												<button
													type="button"
													class="btn btn-primary"
													:class="[{ 'is-loading': isSavingSettings }]"
													@click="saveMetaSettings">
													<i class="material-icons mr-1">save</i>
													<span>{{ $t('Save') }}</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Reseller Settings -->
					<div class="integration-item box p-0">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">{{$t('resellers.settings.title')}}</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="form-group">
											<label><b>{{$t('resellers.settings.commission_type')}}</b></label>
											<select class="form-control" v-model="reseller.commission_type">
												<option value="commission">{{$t('resellers.settings.commission')}}</option>
												<option value="discount">{{$t('resellers.settings.discount')}}</option>
											</select>
										</div>
										<div>
											<div class="form-group text-right">
												<button
													type="button"
													class="btn btn-primary"
													:class="[{ 'is-loading': isSavingReseller }]"
													@click="saveReseller">
													<i class="material-icons">save</i>
													<span>{{ $t('Save') }}</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Domain Verification -->
					<!-- <div class="integration-item">
						<div class="integration-app">
							<div class="wrapper">
								<div class="integration-app-logo">
									<div class="text">Domain Verification</div>
								</div>
							</div>
						</div>
						<div class="integration-accounts">
							<div class="integration-account">
								<div class="wrapper">
									<div class="container-fluid p-0">
										<div class="row">
											<div class="col-md-6">
												<div class="form-group">
													<label><b>Facebook Domain Verification</b></label>
													<input
														class="form-control"
														type="text"
														v-model="userSettings.stock"
													/>

												</div>
											</div>
											<div class="col-12">
												<div class="form-group text-right">
													<button
														type="button"
														class="btn btn-primary"
														:class="{ 'is-loading': isSavingSettings }"
														@click="saveSettings">
														<i class="material-icons">save</i>
														<span>{{ $t('Save') }}</span>
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div> -->
				</div>
			</div>
		</div>
	</div>
	<div class="modals">
	</div>

	<notifications group="general" position="bottom right" />
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MetaTags from '@/components/MetaTags.vue';
import userApi from '@/api/user';
import { duplicateVar, isValidIp } from '@/lib/helper';
import { statusChangeOptions } from '@/lib/statics';

export default {
	name: 'SettingsGeneral',
	components: {
		MetaTags,
	},
	data() {
		return {
			title: 'General',
			autoCancel: {
				enabled: false,
				type: 'day',
				value: 7,
			},
			transactionEmailSetting: {
				enabled: true,
				reply_to: '',
			},
			userSettings: null,
			isSavingAutoCancel: false,
			isSavingReseller: false,
			isSavingEmailTransactionalSetting: false,
			isSavingSettings: false,
			ipAddress: '',
			fbDomainVerification: '',
			instantPayment: {
				status: 'processing',
				logistic_enabled: true,
			},
			isSavingInstant: false,
			statusChangeOptions: duplicateVar(statusChangeOptions),
			blockedIps: [],
			customMetaTags: [],
			reseller: {
				commission_type: 'commission',
			},
		};
	},
	methods: {
		...mapActions({
			setAutoCancel: 'setAutoCancel',
			setReseller: 'setReseller',
			setTransactionalEmail: 'setTransactionalEmail',
			setSettings: 'setSettings',
		}),
		init() {
			this.getAutoCancel();
			this.getReseller();
			this.getEmailTransactional();
			this.getInstantPayment();

			if (!this.userSettings) {
				this.getUserSettings();
			}
		},
		getAutoCancel() {
			if (this.user.auto_cancel_order) {
				const autoCancel = duplicateVar(this.user.auto_cancel_order);
				this.autoCancel = autoCancel;
			}
		},
		getReseller() {
			if (this.user.reseller) {
				const reseller = duplicateVar(this.user.reseller);
				this.reseller = reseller;
			}
		},
		getEmailTransactional() {
			if (this.user.transactional_email) {
				this.transactionEmailSetting = duplicateVar(this.user.transactional_email);
			}
		},
		saveAutoCancel() {
			this.isSavingAutoCancel = true;
			const autoCancel = duplicateVar(this.autoCancel);
			const callback = (response) => {
				// eslint-disable-next-line
				console.log(response);
				this.isSavingAutoCancel = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Auto Cancel options updated',
					type: 'success',
				});
				this.setAutoCancel(autoCancel);
			};
			const errorCallback = () => {
				this.isSavingAutoCancel = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Auto Cancel options not updated, please try again',
					type: 'error',
				});
			};
			userApi.updateAutoCancel(autoCancel, callback, errorCallback);
		},
		saveReseller() {
			this.isSavingReseller = true;
			const reseller = duplicateVar(this.reseller);
			const callback = (response) => {
				// eslint-disable-next-line
				console.log(response);
				this.isSavingReseller = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Reseller options updated',
					type: 'success',
				});
				this.setReseller(reseller);
			};
			const errorCallback = () => {
				this.isSavingReseller = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Reseller options not updated, please try again',
					type: 'error',
				});
			};
			userApi.updateReseller(reseller, callback, errorCallback);
		},
		saveInstant() {
			this.isSavingInstant = true;
			const instantPayment = duplicateVar(this.instantPayment);
			const callback = (response) => {
				// eslint-disable-next-line
				console.log(response);
				this.isSavingInstant = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'E-Payment options updated',
					type: 'success',
				});
				// this.setAutoCancel(instantPayment);
			};
			const errorCallback = () => {
				this.isSavingInstant = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'E-Payment options not updated, please try again',
					type: 'error',
				});
			};
			userApi.updateInstantPayment(instantPayment, callback, errorCallback);
		},
		saveEmailTransactionalSetting() {
			this.isSavingEmailTransactionalSetting = true;
			const transactionEmailSetting = duplicateVar(this.transactionEmailSetting);
			const callback = (response) => {
				// eslint-disable-next-line
				console.log(response);
				this.isSavingEmailTransactionalSetting = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Customer email notification updated',
					type: 'success',
				});
				this.setTransactionalEmail(transactionEmailSetting);
			};
			const errorCallback = () => {
				this.isSavingEmailTransactionalSetting = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Customer email notification not updated, please try again',
					type: 'error',
				});
			};
			userApi.updateTransactionalEmail(transactionEmailSetting, callback, errorCallback);
		},
		saveSettings() {
			this.isSavingSettings = true;
			const settings = duplicateVar(this.userSettings);
			settings.blocked_ip = this.blockedIps;
			const callback = () => {
				this.isSavingSettings = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Settings updated!',
					type: 'success',
				});
				this.setSettings(settings);
			};
			const errorCallback = () => {
				this.isSavingSettings = false;
				this.$notify({
					group: 'general',
					title: 'General',
					text: 'Failed to update settings, please try again',
					type: 'error',
				});
			};
			userApi.updateSettings(settings, callback, errorCallback);
		},
		saveMetaSettings() {
			this.isSavingSettings = true;
			const settings = {
				metas: this.customMetaTags,
			};
			const callback = () => {
				this.isSavingSettings = false;
				this.$notify({
					group: 'general',
					title: 'Custom Meta Tags',
					text: 'Settings updated!',
					type: 'success',
				});
				// this.setSettings(settings);
			};
			const errorCallback = () => {
				this.isSavingSettings = false;
				this.$notify({
					group: 'general',
					title: 'Custom Meta Tags',
					text: 'Failed to update settings, please try again',
					type: 'error',
				});
			};
			userApi.updateSettings(settings, callback, errorCallback);
		},
		getUserSettings() {
			if (this.settings) {
				this.userSettings = duplicateVar(this.settings);

				// Blocked IPs
				const blockedIps = this.settings.blocked_ip ? duplicateVar(this.settings.blocked_ip) : [];
				this.blockedIps = blockedIps;

				// Custom Meta Tags
				const customMetaTags = this.settings.metas ? duplicateVar(this.settings.metas) : [];
				this.customMetaTags = customMetaTags;
			}
		},
		addIp() {
			const ipInput = this.ipAddress.trim();
			const ipAddresses = ipInput.split('\n');
			for (let index = 0; index < ipAddresses.length; index++) {
				const ipAddress = ipAddresses[index].trim();
				const isValid = isValidIp(ipAddress);
				const isNotExist = this.blockedIps.findIndex(ip => ip === ipAddress) === -1;
				if (isValid && isNotExist) {
					this.blockedIps.push(ipAddress);
				}
			}
			// const blockedIp = duplicateVar(this.blockedIp);
			// for (let index = 0; index < ipAddresses.length; index++) {
			// 	const ipAddress = ipAddresses[index].trim();
			// 	const isValid = isValidIp(ipAddress);
			// 	const isNotExist = blockedIp.findIndex(ip => ip === ipAddress) === -1;
			// 	if (isValid && isNotExist) {
			// 		blockedIp.push(ipAddress);
			// 		this.$set(this.userSettings, 'blocked_ip', blockedIp);
			// 	}
			// }
			this.ipAddress = '';
		},
		removeIp(index) {
			this.blockedIps.splice(index, 1);
			// const blockedIp = duplicateVar(this.blockedIp);
			// blockedIp.splice(index, 1);
			// this.$set(this.userSettings, 'blocked_ip', blockedIp);
		},
		getInstantPayment() {
			if (this.user.instant_payment) {
				const instantPayment = duplicateVar(this.user.instant_payment);
				const defaultInstant = {
					status: 'processing',
					logistic_enabled: true,
				};
				this.instantPayment = Object.assign({}, defaultInstant, instantPayment);
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			settings: 'settings',
			isAdmin: 'isAdmin',
			isTester: 'isTester',
			isHasFeature: 'isHasFeature',
		}),
		blockedIp() {
			return this.userSettings && this.userSettings.blocked_ip ? this.userSettings.blocked_ip : [];
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
	},
	watch: {
		user() {
			this.init();
		},
		settings() {
			this.getUserSettings();
		},
	},
};
</script>
